import { parse } from 'yaml';

import { pathTemplateFault, reservedPathFault, RouteBinding } from './identity';
import { resolveIngress } from './ingress';
import { ConsoleState } from './store';
import { RouteDef } from './types';

/**
 * An address the data plane answers on, and which route of which vendor it belongs to.
 *
 * Each address starts with its vendor's name, so two vendors on the same docs endpoint have
 * addresses of their own (`/logicerp/v2/bills`, `/wondersoft/v2/bills`). What is listed here is what is published, so an address typed on the
 * canvas is the address vendors reach, with nothing to add by hand.
 */
export interface PublicEndpoint extends RouteBinding {
  routeKey?: string;
  vendorId: string;
}

export interface EndpointIssue {
  method: string;
  path: string;
  message: string;
}

const tidy = (path: string): string => {
  const trimmed = path.trim().replace(/\/+$/, '');
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

/** The path and method on the flow's Platform HTTP node, when the flow names one. */
export const flowAddress = (flow: string | undefined): { path?: string; method?: string } => {
  if (!flow) return {};
  try {
    const parsed = parse(flow) as { route?: { from?: { uri?: string; parameters?: Record<string, unknown> } } }[];
    for (const entry of Array.isArray(parsed) ? parsed : []) {
      const from = entry?.route?.from;
      if (!from?.uri?.startsWith('platform-http')) continue;
      const path = String(from.parameters?.path ?? from.uri.replace(/^platform-http:?/, '')).trim();
      const method = String(from.parameters?.httpMethodRestrict ?? '')
        .split(',')[0]
        .trim()
        .toUpperCase();
      return { ...(path ? { path } : {}), ...(method ? { method } : {}) };
    }
  } catch {
    return {};
  }
  return {};
};

/**
 * The vendor's name as the first part of every address it is given: the slug Casa knows it by,
 * fixed once anything of the vendor is published. Each vendor's endpoints therefore have
 * addresses of their own, and a vendor's key is checked against the name it was sent under.
 */
export const vendorPrefixOf = (state: ConsoleState, route: RouteDef): string => {
  const vendor = state.vendors.find((item) => item.id === route.vendorId);
  return (vendor?.slug ?? vendor?.id ?? route.vendorId).toLowerCase();
};

/**
 * Where a route is reached: the vendor's name, then the address Casa's API docs give it, and
 * nothing else. A path drawn on a canvas never stands in for one. The platform's own
 * `/ingest/...` is not public.
 */
export const routeAddress = (
  state: ConsoleState,
  route: RouteDef,
): { method: RouteBinding['method']; path: string } => {
  const docs = route.ingress?.path?.trim() ?? '';
  return {
    method: resolveIngress(state, route).method as RouteBinding['method'],
    path: docs ? tidy(`/${vendorPrefixOf(state, route)}${tidy(docs)}`) : '',
  };
};

/** The parts of an endpoint's address that change per call, by the names the docs give them. */
export const addressParameters = (route: RouteDef): string[] => [
  ...new Set([...(route.ingress?.path ?? '').matchAll(/\{([^}]+)\}/g)].map((match) => match[1])),
];

/** The docs address, when a canvas on any scope draws another one. */
export const scopeAddressFault = (
  state: ConsoleState,
  route: RouteDef,
  _scope: string,
  flow: string | undefined,
): string | undefined => {
  if (!route.ingress?.path?.trim()) return undefined;
  const drawn = flowAddress(flow).path;
  if (!drawn || drawn.startsWith('/ingest/')) return undefined;
  const { method, path } = routeAddress(state, route);
  return tidy(drawn) === path
    ? undefined
    : `This endpoint's address is ${method} ${path}, from Casa's API docs. The canvas can't change it.`;
};

const schemeFor = (state: ConsoleState, route: RouteDef): string | undefined =>
  resolveIngress(state, route).authSchemeId;

export const publicEndpoints = (
  state: ConsoleState,
  routeKeyOf?: (route: RouteDef) => string,
): { endpoints: PublicEndpoint[]; issues: EndpointIssue[] } => {
  const endpoints: PublicEndpoint[] = [];
  const issues: EndpointIssue[] = [];
  const key = (method: string, path: string) => `${method} ${path}`;

  /* An answered endpoint has nothing to publish: it is live the moment it names its service. */
  const published = state.routes.filter(
    (route) =>
      (route.upstream && !route.signInInAddress) ||
      state.revisions.some((revision) => revision.routeId === route.id && revision.live),
  );
  for (const route of published) {
    const { method, path } = routeAddress(state, route);
    const fault = pathTemplateFault(path) ?? reservedPathFault(path, state);
    if (fault) {
      issues.push({ method, path, message: `${route.name}: ${fault}` });
      continue;
    }
    const sharing = endpoints.filter((item) => key(item.method, item.path) === key(method, path));
    if (sharing.some((item) => item.vendorId === route.vendorId)) {
      if (!sharing.some((item) => item.routeId === route.id)) {
        issues.push({
          method,
          path,
          message: `${route.name}: this vendor already has another endpoint at this address.`,
        });
      }
      continue;
    }
    const authSchemeId = schemeFor(state, route);
    if (sharing.length && (sharing[0].authSchemeId ?? '') !== (authSchemeId ?? '')) {
      issues.push({
        method,
        path,
        message: `${route.name}: vendors sharing an address must sign in the same way, and this one does not.`,
      });
      continue;
    }
    endpoints.push({
      id: `rb-${route.id}`,
      method,
      path,
      routeId: route.id,
      vendorId: route.vendorId,
      ...(authSchemeId ? { authSchemeId } : {}),
      ...(route.upstream ? { upstream: route.upstream } : {}),
      ...(route.format === 'xml' ? { format: 'xml' as const } : {}),
      ...(route.isBatch ? { batch: true } : {}),
      prefix: vendorPrefixOf(state, route),
      ...(routeKeyOf ? { routeKey: routeKeyOf(route) } : {}),
    });
  }

  return { endpoints, issues };
};
